import React from 'react';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Volume2, FileText } from 'lucide-react';
import { ALERT_TONES, ALERT_DURATIONS } from '../../constants/alertTones';

const CARD_COLOR_DURATION_OPTIONS = [
  { id: '5s', label: '5 seconds' },
  { id: '10s', label: '10 seconds' },
  { id: '30s', label: '30 seconds' },
  { id: 'until_click', label: 'Persistent until device card click' },
  { id: 'custom', label: 'Custom seconds' },
];

// Play test tone — same WebAudio synth as EventNotificationBell.playAlertTone().
export const playTestTone = (toneId) => {
  const tone = ALERT_TONES.find(t => t.id === toneId);
  if (!tone || tone.freq === 0) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    if (toneId === 'urgent_siren') {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sawtooth'; gain.gain.value = 0.3;
      osc.frequency.setValueAtTime(900, now);
      osc.frequency.linearRampToValueAtTime(1400, now + 0.15);
      osc.frequency.linearRampToValueAtTime(900, now + 0.3);
      osc.frequency.linearRampToValueAtTime(1400, now + 0.45);
      osc.frequency.linearRampToValueAtTime(900, now + 0.6);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
      osc.start(now); osc.stop(now + 0.65);
    } else if (toneId === 'triple_beep') {
      for (let i = 0; i < 3; i++) {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = tone.freq; o.type = 'sine'; g.gain.value = 0.3;
        g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.18 + 0.12);
        o.start(now + i * 0.18); o.stop(now + i * 0.18 + 0.15);
      }
    } else if (toneId === 'continuous') {
      for (let i = 0; i < 3; i++) {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = tone.freq; o.type = 'square'; g.gain.value = 0.2;
        g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.4 + 0.25);
        o.start(now + i * 0.4); o.stop(now + i * 0.4 + 0.3);
      }
    } else if (toneId === 'low_warning') {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = tone.freq; osc.type = 'triangle'; gain.gain.value = 0.3;
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      osc.start(now); osc.stop(now + 0.55);
    } else if (toneId === 'alarm') {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = tone.freq; osc.type = 'sine'; gain.gain.value = 0.3;
      gain.gain.exponentialRampToValueAtTime(0.001, now + tone.duration / 1000);
      osc.start(now); osc.stop(now + tone.duration / 1000 + 0.05);
      const osc2 = ctx.createOscillator(); const gain2 = ctx.createGain();
      osc2.connect(gain2); gain2.connect(ctx.destination);
      osc2.frequency.value = tone.freq; osc2.type = 'sine'; gain2.gain.value = 0.3;
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.25 + tone.duration / 1000);
      osc2.start(now + 0.25); osc2.stop(now + 0.25 + tone.duration / 1000 + 0.05);
    } else {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = tone.freq; osc.type = 'sine'; gain.gain.value = 0.3;
      gain.gain.exponentialRampToValueAtTime(0.001, now + tone.duration / 1000);
      osc.start(now); osc.stop(now + tone.duration / 1000 + 0.05);
    }
    setTimeout(() => ctx.close(), 2000);
  } catch { /* Audio not available */ }
};

// Shared Notifications tab body — used by Event Rules AND Analytics > Alerts config
// so both surfaces stay byte-for-byte consistent (System Message, Sound, Push,
// Email + template, SMS + template, Change Device Card Colour).
export const NotificationsConfig = ({
  notifications,
  onChange,
  hasPushNotif,
  hasEmailNotif,
  hasSmsNotif,
  pushDisabledReason,
  emailDisabledReason,
  smsDisabledReason,
  notifTemplates = [],
  onManageTemplate,
  supportsTransitionCardColorTiming = false,
  showDeviceCardColor = true,
}) => {
  const n = notifications || {};
  return (
    <div className="space-y-3">
      {/* System Message */}
      <div className="p-3 rounded-lg bg-muted space-y-2">
        <div className="flex items-center justify-between">
          <div><p className="font-medium text-sm">System Message</p><p className="text-xs text-muted-foreground">Show in-app notification</p></div>
          <Switch checked={n.system_message !== false} onCheckedChange={(v) => onChange('system_message', v)} data-testid="notif-system-message" />
        </div>
        {n.system_message !== false && (
          <div className="flex items-center gap-2 pl-1">
            <Checkbox checked={n.auto_hide || false} onCheckedChange={(v) => onChange('auto_hide', !!v)} data-testid="notif-auto-hide-checkbox" />
            <span className="text-xs text-muted-foreground cursor-pointer select-none" onClick={() => onChange('auto_hide', !n.auto_hide)} data-testid="notif-auto-hide-label">Auto-hide message (otherwise stays until confirmed by user)</span>
          </div>
        )}
      </div>

      {/* Sound Alert */}
      <div className="p-3 rounded-lg bg-muted space-y-2">
        <div className="flex items-center justify-between">
          <div><p className="font-medium text-sm">Sound Alert</p><p className="text-xs text-muted-foreground">Play alert sound when triggered</p></div>
          <Switch checked={n.sound_alert || false} onCheckedChange={(v) => onChange('sound_alert', v)} data-testid="notif-sound" />
        </div>
        {n.sound_alert && (
          <>
            <div className="flex items-center gap-2">
              <Select value={n.sound_file || 'default'} onValueChange={(v) => onChange('sound_file', v)}>
                <SelectTrigger className="h-8 text-sm flex-1" data-testid="notif-sound-tone"><SelectValue /></SelectTrigger>
                <SelectContent>{ALERT_TONES.map(t => (<SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>))}</SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 gap-1" onClick={() => playTestTone(n.sound_file || 'default')} disabled={n.sound_file === 'none'} data-testid="play-sound-btn">
                <Volume2 className="w-3.5 h-3.5" /> Test
              </Button>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Alert Duration</Label>
              <Select value={n.sound_duration || 'once'} onValueChange={(v) => onChange('sound_duration', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{ALERT_DURATIONS.map(d => (<SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>))}</SelectContent>
              </Select>
              {n.sound_duration === 'until_dismissed' && (
                <p className="text-[10px] text-amber-600 dark:text-amber-400">Alert will keep playing until you dismiss the notification.</p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Push Notification */}
      <div className={`p-3 rounded-lg border space-y-2 ${!hasPushNotif ? 'opacity-40 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch checked={hasPushNotif ? (n.push_notification || false) : false} onCheckedChange={(v) => onChange('push_notification', v)} data-testid="notif-push" />
            <div><p className="font-medium text-sm">Push Notification</p><p className="text-xs text-muted-foreground">Browser push notification</p></div>
          </div>
          {!hasPushNotif && <Badge variant="secondary" className="text-xs">{pushDisabledReason === 'user_setting' ? 'Disabled in Settings' : 'Privilege Required'}</Badge>}
        </div>
      </div>

      {/* Email */}
      <div className={`p-3 rounded-lg border space-y-2 ${!hasEmailNotif ? 'opacity-40 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch checked={hasEmailNotif ? (n.email_enabled || false) : false} onCheckedChange={(v) => onChange('email_enabled', v)} data-testid="notif-email-toggle" />
            <div><p className="font-medium text-sm">Email Notification</p><p className="text-xs text-muted-foreground">For multiple emails, separate with commas</p></div>
          </div>
          {!hasEmailNotif && <Badge variant="secondary" className="text-xs">{emailDisabledReason === 'user_setting' ? 'Disabled in Settings' : 'Privilege Required'}</Badge>}
        </div>
        {n.email_enabled && hasEmailNotif && (
          <div className="space-y-2">
            <Input value={n.email_addresses || ''} onChange={(e) => onChange('email_addresses', e.target.value)} placeholder="email@example.com, another@example.com" className="text-base md:text-sm" />
            <div className="flex items-center gap-2">
              <Label className="text-xs shrink-0">Template:</Label>
              <Select value={n.email_template_id || '_default'} onValueChange={(v) => onChange('email_template_id', v === '_default' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs flex-1" data-testid="notif-email-template"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_default">Default (Built-in)</SelectItem>
                  {notifTemplates.filter(t => t.template_type === 'email').map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 text-xs shrink-0" data-testid="notif-email-manage-btn" onClick={() => onManageTemplate && onManageTemplate('email')}>
                <FileText className="w-3 h-3 mr-1" /> Manage
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* SMS */}
      <div className={`p-3 rounded-lg border space-y-2 ${!hasSmsNotif ? 'opacity-40 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch checked={hasSmsNotif ? (n.sms_enabled || false) : false} onCheckedChange={(v) => onChange('sms_enabled', v)} data-testid="notif-sms-toggle" />
            <div><p className="font-medium text-sm">SMS Notification</p><p className="text-xs text-muted-foreground">For multiple numbers, separate with commas</p></div>
          </div>
          {!hasSmsNotif && <Badge variant="secondary" className="text-xs">{smsDisabledReason === 'user_setting' ? 'Disabled in Settings' : 'Privilege Required'}</Badge>}
        </div>
        {n.sms_enabled && hasSmsNotif && (
          <div className="space-y-2">
            <Input value={n.sms_numbers || ''} onChange={(e) => onChange('sms_numbers', e.target.value)} placeholder="+61400000000, +61400000001" className="text-base md:text-sm" />
            <div className="flex items-center gap-2">
              <Label className="text-xs shrink-0">Template:</Label>
              <Select value={n.sms_template_id || '_default'} onValueChange={(v) => onChange('sms_template_id', v === '_default' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs flex-1" data-testid="notif-sms-template"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_default">Default (Built-in)</SelectItem>
                  {notifTemplates.filter(t => t.template_type === 'sms').map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 text-xs shrink-0" data-testid="notif-sms-manage-btn" onClick={() => onManageTemplate && onManageTemplate('sms')}>
                <FileText className="w-3 h-3 mr-1" /> Manage
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Device Card Colour Change */}
      {showDeviceCardColor && (
      <div className="p-3 rounded-lg border space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch checked={n.change_device_card_color || false} onCheckedChange={(v) => onChange('change_device_card_color', v)} data-testid="notif-card-color" />
            <div><p className="font-medium text-sm">Change Device Card Colour</p><p className="text-xs text-muted-foreground">Highlight device card on event trigger</p></div>
          </div>
        </div>
        {n.change_device_card_color && (
          <div className="space-y-2 pl-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Label className="text-xs w-16">Light:</Label>
                <input type="color" value={n.device_card_color || '#ef4444'} onChange={(e) => onChange('device_card_color', e.target.value)} className="w-8 h-8 rounded border cursor-pointer" data-testid="notif-card-color-light" />
                <span className="text-xs text-muted-foreground">{n.device_card_color || '#ef4444'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs w-16">Dark:</Label>
                <input type="color" value={n.device_card_color_dark || '#5e2230'} onChange={(e) => onChange('device_card_color_dark', e.target.value)} className="w-8 h-8 rounded border cursor-pointer" data-testid="notif-card-color-dark" />
                <span className="text-xs text-muted-foreground">{n.device_card_color_dark || '#5e2230'}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 h-6 rounded border" style={{ backgroundColor: n.device_card_color || '#ef4444' }} />
              <div className="flex-1 h-6 rounded border border-slate-600" style={{ backgroundColor: n.device_card_color_dark || '#5e2230' }} />
            </div>
            <p className="text-[10px] text-muted-foreground">Left preview = Light mode, Right preview = Dark mode</p>
            {supportsTransitionCardColorTiming && (
              <div className="space-y-2 pt-2 border-t" data-testid="notif-transition-card-colour-timing">
                <div>
                  <p className="font-medium text-xs">Transition Event Card Colour Timing</p>
                  <p className="text-[10px] text-muted-foreground">Applies to transition-style events such as Zone In/Out, Ignition, Moving/Stopped, and future route transitions.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Timing Mode</Label>
                    <Select
                      value={n.transition_card_color_mode || '5s'}
                      onValueChange={(v) => {
                        onChange('transition_card_color_mode', v);
                        if (v === '5s') onChange('transition_card_color_seconds', 5);
                        if (v === '10s') onChange('transition_card_color_seconds', 10);
                        if (v === '30s') onChange('transition_card_color_seconds', 30);
                      }}
                    >
                      <SelectTrigger data-testid="notif-transition-card-colour-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CARD_COLOR_DURATION_OPTIONS.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {n.transition_card_color_mode === 'custom' && (
                    <div className="space-y-1">
                      <Label className="text-xs">Custom Seconds</Label>
                      <Input
                        type="number"
                        min="1"
                        value={n.transition_card_color_seconds || 5}
                        onChange={(e) => onChange('transition_card_color_seconds', Math.max(1, parseInt(e.target.value, 10) || 5))}
                        data-testid="notif-transition-card-colour-custom-seconds"
                      />
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Default for new transition rules: 5 seconds. In persistent mode, the latest event colour remains until that device card is clicked in Live Tracking.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default NotificationsConfig;
